/**
 * "Anpassa" (pedagogy spec §6.3.4, §13 Q21): a small form for today's session, with a link to the
 * full practice form for choosing moments and question types. Cards on or off and their cap, the
 * number of re-tests, a new topic on or off and which one, and "hoppa över det här ämnet
 * framöver" (the persistent skip list). Saving hands the settings and the skip list back; the
 * caller stores them (`mf.today.settings`, `mf.today.skipTopics`) and the plan refetches.
 */
import { useState } from "react";
import { Link } from "react-router";
import { useTopicsAndChapters } from "@client/api/topics";
import { useLocalizedText } from "@client/components/Localized";
import { Button, Checkbox, Modal, Select } from "@client/components/ui";
import { useT } from "@client/i18n";
import {
  CARD_LIMITS,
  MAX_RETESTS,
  todayKey,
  withoutOverride,
  type CardLimit,
  type TodaySettings,
} from "./settings";
import type { TodayNewTopicLecture } from "./types";

export interface AdjustDialogProps {
  open: boolean;
  onClose: () => void;
  settings: TodaySettings;
  skip: readonly string[];
  /** The topic the plan picked on its own, so "hoppa över" can name it. */
  plannedTopicId: string | null;
  /** The lecture behind the plan's own pick (P1-3), which names the automatic choice. */
  plannedLecture?: TodayNewTopicLecture | null;
  onSave: (settings: TodaySettings, skip: string[]) => void;
}

const RETEST_COUNTS = Array.from({ length: MAX_RETESTS + 1 }, (_, n) => n);

export function AdjustDialog(props: AdjustDialogProps) {
  // The form is mounted only while the dialog is open, so it starts from the stored values each time.
  return props.open ? <AdjustForm {...props} /> : null;
}

function AdjustForm({
  onClose,
  settings,
  skip,
  plannedTopicId,
  plannedLecture,
  onSave,
}: AdjustDialogProps) {
  const t = useT();
  const localize = useLocalizedText();
  const topics = useTopicsAndChapters();
  const [cards, setCards] = useState(settings.cards);
  const [cardLimit, setCardLimit] = useState<CardLimit>(settings.cardLimit);
  const [retests, setRetests] = useState(settings.retests);
  const [includeNew, setIncludeNew] = useState(settings.includeNew);
  const [newTopic, setNewTopic] = useState(settings.newTopic?.id ?? "");
  const [skipTopic, setSkipTopic] = useState(false);

  const choices = (topics.data?.topics ?? []).filter((topic) => !topic.cursory);
  const nameOf = (id: string) => {
    const topic = topics.data?.topics.find((candidate) => candidate.id === id);
    return topic ? localize(topic.name) : id;
  };
  // What "hoppa över" would pass over: the topic picked by hand, else the one the plan chose.
  const target = newTopic || plannedTopicId;

  function save() {
    let nextSkip = [...skip];
    let chosen = newTopic;
    if (includeNew && skipTopic && target) {
      nextSkip = [...new Set([...nextSkip, target])];
      // A topic that was just skipped is no longer the hand-picked one.
      if (chosen === target) chosen = "";
    }
    const base = withoutOverride(settings);
    onSave(
      {
        ...base,
        cards,
        cardLimit,
        retests,
        includeNew,
        ...(includeNew && chosen ? { newTopic: { id: chosen, dayKey: todayKey() } } : {}),
      },
      nextSkip,
    );
    onClose();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("practice.today.adjust.title")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={save}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Checkbox
            label={t("practice.today.adjust.includeCards")}
            checked={cards}
            onChange={(event) => setCards(event.target.checked)}
          />
          <Select
            label={t("practice.today.adjust.cardLimit")}
            disabled={!cards}
            value={String(cardLimit)}
            onChange={(event) => setCardLimit(Number(event.target.value) as CardLimit)}
            options={CARD_LIMITS.map((n) => ({ value: String(n), label: String(n) }))}
          />
        </div>

        <Select
          label={t("practice.today.adjust.retests")}
          value={String(retests)}
          onChange={(event) => setRetests(Number(event.target.value))}
          options={RETEST_COUNTS.map((n) => ({ value: String(n), label: String(n) }))}
        />

        <div className="space-y-2">
          <Checkbox
            label={t("practice.today.adjust.includeNew")}
            checked={includeNew}
            onChange={(event) => setIncludeNew(event.target.checked)}
          />
          <Select
            label={t("practice.today.adjust.newTopic")}
            disabled={!includeNew}
            value={newTopic}
            onChange={(event) => setNewTopic(event.target.value)}
            options={[
              {
                value: "",
                label: plannedLecture
                  ? t(
                      plannedLecture.role === "prepare"
                        ? "practice.today.adjust.newTopicLecture"
                        : "practice.today.adjust.newTopicCatchUp",
                      { id: plannedLecture.id },
                    )
                  : t("practice.today.adjust.newTopicAuto"),
              },
              ...choices.map((topic) => ({
                value: topic.id,
                label: t("practice.today.adjust.topicOption", {
                  chapter: topic.chapter,
                  topic: localize(topic.name),
                }),
              })),
            ]}
          />
          {includeNew && target && (
            <Checkbox
              label={t("practice.today.adjust.skipTopic")}
              hint={t("practice.today.adjust.skipTopicHint", { topic: nameOf(target) })}
              checked={skipTopic}
              onChange={(event) => setSkipTopic(event.target.checked)}
            />
          )}
        </div>

        <p className="text-sm">
          <Link
            to="/practice"
            className="font-medium text-accent-800 underline underline-offset-2 hover:no-underline dark:text-accent-300"
          >
            {t("practice.today.adjust.fullForm")}
          </Link>
        </p>
      </div>
    </Modal>
  );
}
